import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Zap,
  Settings, Menu, X, UserCircle, BarChart3, Sun, Moon, LogOut, PhoneCall
} from 'lucide-react';
import axios from 'axios';
import { useTheme } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/clients',   icon: Users,           label: 'Clients' },
  { to: '/automations', icon: Zap,           label: 'Automations' },
  { to: '/callbacks', icon: PhoneCall,       label: 'À rappeler', badgeKey: 'callbacks' },
  { to: '/senders',   icon: UserCircle,       label: 'Expéditeurs' },
  { to: '/monitoring', icon: BarChart3,       label: 'Monitoring' },
  { to: '/settings',  icon: Settings,         label: 'Paramètres' },
];

// Marque blanche (#11) : logo du compte s'il existe, sinon son nom en texte,
// sinon le logo WorkitDoit. L'écran de login, lui, garde toujours WorkitDoit.
const Logo = ({ companyName, companyLogo }) => (
  <div className="px-5 py-4 border-b border-white/8">
    {companyLogo ? (
      <img
        src={companyLogo}
        alt={companyName || 'Logo'}
        className="w-36 max-h-12 h-auto object-contain"
      />
    ) : companyName ? (
      <p className="text-white text-lg font-semibold font-heading leading-tight truncate">
        {companyName}
      </p>
    ) : (
      <img
        src="/logo-white.png"
        alt="WorkitDoit"
        className="w-36 h-auto object-contain"
        style={{ filter: 'brightness(1)' }}
      />
    )}
    <p className="text-xs text-white/40 mt-1.5 tracking-wide">CRM & Automation</p>
  </div>
);

const NavItem = ({ item, onClick, badgeCount }) => (
  <li>
    <NavLink
      to={item.to}
      end={item.to === '/dashboard'}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
          isActive
            ? 'bg-white/10 text-white font-medium border-l-2 border-wid-red pl-[10px]'
            : 'text-white/55 hover:text-white/90 hover:bg-white/6'
        }`
      }
      data-testid={`nav-${item.label.toLowerCase()}`}
    >
      <item.icon size={17} strokeWidth={1.8} />
      <span className="flex-1">{item.label}</span>
      {badgeCount > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold bg-wid-red text-white"
          data-testid={`nav-badge-${item.label.toLowerCase()}`}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </NavLink>
  </li>
);

const ThemeToggle = ({ theme, toggleTheme }) => (
  <button
    onClick={toggleTheme}
    className="flex items-center gap-2 px-3 py-2 rounded-md text-white/45 hover:text-white/80 hover:bg-white/6 transition-all text-xs w-full"
    data-testid="theme-toggle"
    title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
  >
    {theme === 'dark'
      ? <><Sun size={15} strokeWidth={1.8} /><span>Mode clair</span></>
      : <><Moon size={15} strokeWidth={1.8} /><span>Mode sombre</span></>
    }
  </button>
);

export const DashboardLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [callbacksPending, setCallbacksPending] = useState(0);
  const [branding, setBranding] = useState({ name: null, logo: null });
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/callbacks/count`);
        if (!cancelled) setCallbacksPending(res.data?.pending || 0);
      } catch {
        if (!cancelled) setCallbacksPending(0);
      }
      // Marque blanche : rechargée au même rythme que le badge, pour que le
      // bandeau se mette à jour dès qu'on quitte l'écran Paramètres.
      try {
        const res = await axios.get(`${API}/account-settings`);
        if (!cancelled) setBranding({
          name: res.data?.company_name || null,
          logo: res.data?.company_logo || null,
        });
      } catch {
        /* pas de marque personnalisée → on garde WorkitDoit */
      }
    };
    load();
    // refresh on route change so the badge updates after marking done
    return () => { cancelled = true; };
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('wid-token');
    navigate('/login');
  };

  const badgeFor = (item) => (item.badgeKey === 'callbacks' ? callbacksPending : 0);

  const sidebarContent = (isMobile = false) => (
    <>
      <Logo companyName={branding.name} companyLogo={branding.logo} />
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        <ul className="space-y-0.5">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              item={item}
              badgeCount={badgeFor(item)}
              onClick={isMobile ? () => setMobileMenuOpen(false) : undefined}
            />
          ))}
        </ul>
      </nav>
      <div className="px-3 pb-4 pt-2 border-t border-white/8 space-y-1">
        <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-red-400/70 hover:text-red-400 hover:bg-white/6 transition-all text-xs w-full"
        >
          <LogOut size={15} strokeWidth={1.8} />
          <span>Déconnexion</span>
        </button>
        <p className="text-[10px] text-white/25 px-3 pt-1">v2.6 · WorkitDoit</p>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background transition-colors duration-200">

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4"
        style={{ backgroundColor: 'hsl(var(--sidebar))' }}>
        {branding.logo ? (
          <img src={branding.logo} alt={branding.name || 'Logo'} className="h-6 w-auto object-contain" />
        ) : branding.name ? (
          <span className="text-white text-sm font-semibold truncate max-w-[60%]">{branding.name}</span>
        ) : (
          <img src="/logo-white.png" alt="WorkitDoit" className="h-6 w-auto object-contain" />
        )}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white/70 hover:text-white p-1"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-56 fixed h-full left-0 top-0 z-40 flex-col"
        style={{ backgroundColor: 'hsl(var(--sidebar))' }}
      >
        {sidebarContent()}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        >
          <aside
            className="w-56 h-full flex flex-col"
            style={{ backgroundColor: 'hsl(var(--sidebar))' }}
            onClick={(e) => e.stopPropagation()}
          >
            {sidebarContent(true)}
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-56 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
